import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { inBasket, planEstimate } from '@sp/shared'
import { estimateText, formatQty, itemsCount, tripName, unitPrice } from '../../lib/format.ts'
import { dealNote } from '../../lib/offers.ts'
import { Button } from '../../components/ui.tsx'
import { ratesFor, routeOf, sectionsFor, type TripViewProps } from './shared.ts'

/**
 * Seznam k vytisteni (navrh, faze 3): kdyz chce nekdo papir, nebo jako
 * pojistka, kdyz nejede sit ani telefon. Na obrazovce vypada jako list papiru,
 * pri tisku zmizi pozadi, bocni panel i tlacitka.
 */
export function PrintView({ trip, stores, fx }: TripViewProps) {
  const navigate = useNavigate()
  const route = routeOf(trip, stores)
  const sections = sectionsFor(trip, stores).filter((section) => section.items.length > 0)
  const estimate = estimateText(planEstimate(trip.items, ratesFor(trip, fx)), route.map((store) => store.currency))
  const name = tripName(trip, stores)
  const title = [name.title, name.date].filter(Boolean).join(' – ')

  // Titulek stranky je nazev souboru pri „Ulozit jako PDF".
  useEffect(() => {
    const previous = document.title
    document.title = `${title} – Sériové nákupy`
    return () => {
      document.title = previous
    }
  }, [title])

  const summary = [route.map((store) => store.name).join(' → '), itemsCount(trip.items.length), estimate && `odhad ${estimate}`]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="print-page">
      <div className="print-actions no-print">
        <Button icon="chevron_left" onClick={() => navigate(`/nakup/${trip.id}`)}>
          Zpět na nákup
        </Button>
        <Button kind="primary" icon="printer" onClick={() => window.print()}>
          Vytisknout
        </Button>
      </div>

      <article className="print-sheet">
        <header className="print-head">
          <h1>{title}</h1>
          {summary && <p>{summary}</p>}
        </header>

        {sections.length === 0 && <p className="print-empty">Seznam je prázdný.</p>}
        {sections.map((section) => (
          <section key={section.key} className="print-section">
            <h2>
              {section.title}
              <span>{itemsCount(section.items.length)}</span>
            </h2>
            <table className="print-table">
              <tbody>
                {section.items.map((item) => (
                  <tr key={item.id} className={inBasket(item) ? 'is-done' : undefined}>
                    <td className="print-check-cell">
                      <span className="print-check" aria-label={inBasket(item) ? 'v košíku' : 'nekoupeno'} />
                    </td>
                    <td>
                      <span className="print-name">{item.name}</span>
                      {item.note && <span className="print-note">{item.note}</span>}
                    </td>
                    <td className="print-qty">{formatQty(item.qty, item.unit)}</td>
                    <td className="print-price">
                      {unitPrice(item.expectedPriceMinor, item.expectedCurrency, item.unit) ?? ''}
                      {item.deal && <span className="print-note">{dealNote(item.deal)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}

        <footer className="print-foot">
          Vytištěno {new Date().toLocaleDateString('cs-CZ')} ze Sériových nákupů
        </footer>
      </article>
    </div>
  )
}
